import { Tooltip as AntTooltip, theme } from 'antd'
import { Cell, Pie, PieChart, Tooltip } from 'recharts'

import { formatBytes, pluralUk } from '@/Containers/DiskSection/DiskContainer/Hooks'
import { formatInt, useChartPalette, useOtherColor } from '@/Ship/Components/Dashboard/Overview/shared'
import { classNames } from '@/Ship/Helpers/classNames'

import { categorySegments, readableTextOn, stateSlices } from '../Helpers/diskAdminCharts'
import { formatPercent, quotaTone } from '../Helpers/diskAdminFormat'

import type { DiskAdminByState, DiskAdminCategory } from '../Contracts/DiskAdmin'

import overview from '@/Ship/Components/Dashboard/Overview/Overview.module.css'
import styles from './DiskAdmin.module.css'

export const filesLabel = (count: number): string =>
    `${formatInt(count)} ${pluralUk(count, 'файл', 'файли', 'файлів')}`

const shareText = (share: number): string => (share > 0 && share < 1 ? '< 1 %' : `${Math.round(share)} %`)

/**
 * «Чим зайнято» — складена смуга за сімома категоріями (`DISK-006` §5.3):
 * довжини відрізків на одній осі порівнювати легше, ніж кути. Колір
 * прив'язаний до категорії; «Інші» — нейтральний колір залишку, не серія.
 *
 * Читабельність (вартовий графіків): легенда — під смугою, а не поверх неї;
 * підказка сегмента називає категорію, обсяг і кількість файлів; сегменти
 * менше 2 % не підписуються, але є в легенді; нульові категорії в легенді
 * лишаються з нулем — видно, що відео немає, а не що його забули.
 */
export const CategoryStack = ({ rows }: { rows: readonly DiskAdminCategory[] }) => {
    const palette = useChartPalette()
    const otherColor = useOtherColor()
    const { segments, total } = categorySegments(rows, palette, otherColor)

    return (
        <div data-chart="disk-categories">
            <div className={classNames(styles['stack'])} role="img" aria-label="Чим зайнято місце за типами файлів">
                {segments
                    .filter((segment) => segment.bytes > 0)
                    .map((segment) => {
                        const text = readableTextOn(segment.color)

                        return (
                            <AntTooltip
                                key={segment.category}
                                title={`${segment.label} · ${formatBytes(segment.bytes)} · ${filesLabel(segment.files)} · ${shareText(segment.share)}`}
                            >
                                <div
                                    className={classNames(styles['segment'])}
                                    data-segment={segment.category}
                                    style={{
                                        flexGrow: segment.bytes,
                                        flexBasis: 0,
                                        background: segment.color,
                                        ...(text ? { color: text } : {}),
                                    }}
                                >
                                    {segment.labelled && text ? shareText(segment.share) : null}
                                </div>
                            </AntTooltip>
                        )
                    })}
            </div>
            <div className={classNames(styles['stackLegend'])}>
                {segments.map((segment) => (
                    <div
                        key={segment.category}
                        className={classNames(styles['stackLegendItem'], segment.bytes === 0 && styles['legendEmpty'])}
                        data-legend={segment.category}
                    >
                        <span className={classNames(styles['swatch'])} style={{ background: segment.color }} />
                        <span
                            className={classNames(overview['barLabel'], segment.bytes === 0 && styles['legendEmpty'])}
                            title={`${segment.label}: ${filesLabel(segment.files)}`}
                        >
                            {segment.label}
                        </span>
                        <span className={classNames(overview['barValue'])}>
                            {formatBytes(segment.bytes)}
                            <span className={classNames(overview['barMuted'])}>
                                {total > 0 ? shareText(segment.share) : ''}
                            </span>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

const DONUT_SIZE = 180

/**
 * «Стан місця» — кільце з чотирьох частин одного цілого: поточні версії,
 * старі версії, кошик, резерв завантажень. Підсумок — у центрі; легенда —
 * поруч, а не поверх даних; підказка називає частину (зразок — `ClientsDonut`
 * «Робочого столу»).
 */
export const StateDonut = ({ byState }: { byState: DiskAdminByState }) => {
    const { token } = theme.useToken()
    const palette = useChartPalette()
    const { slices, total } = stateSlices(byState, palette)
    const drawn = slices.filter((slice) => slice.value > 0)

    return (
        <div className={classNames(overview['donut'], styles['stateDonut'])} data-chart="disk-state">
            <div className={classNames(overview['donutChart'])} style={{ width: DONUT_SIZE, height: DONUT_SIZE }}>
                <PieChart width={DONUT_SIZE} height={DONUT_SIZE}>
                    <Pie
                        data={drawn.length > 0 ? drawn : [{ name: 'Порожньо', value: 1, color: token.colorFillTertiary }]}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={60}
                        outerRadius={86}
                        paddingAngle={drawn.length > 1 ? 2 : 0}
                        stroke={token.colorBgContainer}
                        strokeWidth={2}
                        isAnimationActive={false}
                    >
                        {(drawn.length > 0 ? drawn : [{ key: 'empty', color: token.colorFillTertiary }]).map((slice) => (
                            <Cell key={slice.key} fill={slice.color} />
                        ))}
                    </Pie>
                    {drawn.length > 0 ? (
                        <Tooltip
                            formatter={(value: number, name: string) => [
                                `${formatBytes(Number(value))} · ${shareText(total > 0 ? (Number(value) / total) * 100 : 0)}`,
                                name,
                            ]}
                            contentStyle={{
                                background: token.colorBgElevated,
                                borderColor: token.colorBorderSecondary,
                                borderRadius: token.borderRadiusLG,
                                fontSize: 12,
                            }}
                            itemStyle={{ color: token.colorText }}
                        />
                    ) : null}
                </PieChart>
                <div className={classNames(overview['donutCenter'])}>
                    <span className={classNames(overview['donutTotal'])} data-donut-total>
                        {formatBytes(total)}
                    </span>
                    <span className={classNames(overview['donutCaption'])}>зайнято</span>
                </div>
            </div>
            <div className={classNames(overview['donutLegend'])}>
                {slices.map((slice) => (
                    <div key={slice.key} className={classNames(overview['donutRow'])} data-legend={slice.key}>
                        <span className={classNames(overview['swatch'])} style={{ background: slice.color }} />
                        <span className={classNames(overview['barLabel'])} title={slice.name}>
                            {slice.name}
                        </span>
                        <span className={classNames(overview['barValue'])}>
                            {formatBytes(slice.value)}
                            <span className={classNames(overview['barMuted'])}>{total > 0 ? shareText(slice.share) : ''}</span>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

/**
 * Тонка смуга заповнення квоти (таблиця, топ, шапка картки): колір — за
 * порогами сповіщень (< 80 % — перша серія палітри, 80–95 % — попередження,
 * ≥ 95 % — помилка). Без квоти — лише обсяг і «ліміту немає».
 */
export const QuotaBar = ({ used, quota, percent }: { used: number; quota: number | null; percent: number | null }) => {
    const { token } = theme.useToken()
    const [primary] = useChartPalette()
    // Бек округлює відсоток до цілого: 2 МБ з 10 ГБ — «0 %»; для підпису й порогу рахуємо точно
    const exact = quota !== null && quota > 0 ? (used / quota) * 100 : percent
    const tone = quotaTone(exact)
    const color = tone === 'error' ? token.colorError : tone === 'warning' ? token.colorWarning : primary

    return (
        <div className={classNames(styles['quota'])} data-quota-tone={tone}>
            <div className={classNames(styles['quotaText'])}>
                <span>
                    {formatBytes(used)}
                    <span className={classNames(styles['quotaMuted'])}>
                        {quota !== null ? ` / ${formatBytes(quota)}` : ' · без ліміту'}
                    </span>
                </span>
                {quota !== null ? <span className={classNames(styles['quotaMuted'])}>{formatPercent(exact)}</span> : null}
            </div>
            {quota !== null ? (
                <div className={classNames(styles['quotaTrack'])}>
                    {used > 0 ? (
                        <div
                            className={classNames(styles['quotaFill'])}
                            style={{ width: `${Math.min(100, Math.max(0, exact ?? 0))}%`, background: color }}
                        />
                    ) : null}
                </div>
            ) : null}
        </div>
    )
}
